import { useRef, useState } from "react";
import { ImagePlus, Pencil, Trash2 } from "lucide-react";
import {
  Box,
  Button,
  Center,
  Flex,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useAuth } from "../../services/auth/AuthProvider";
import {
  deleteBrandImage,
  uploadBrandImage,
  type BrandImageType,
} from "../../services/api/publishingHouse";

export function BrandManager() {
  const toast = useToast();
  const { publishingHouse, refreshPublishingHouse } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [busy, setBusy] = useState<BrandImageType | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const bannerInput = useRef<HTMLInputElement>(null);

  if (!publishingHouse?.id) return null;
  const logoUrl = publishingHouse.logoUrl;
  const bannerUrl = publishingHouse.bannerUrl;

  async function run(type: BrandImageType, action: () => Promise<unknown>) {
    setBusy(type);
    try {
      await action();
      await refreshPublishingHouse();
      toast({ description: "Оновлено", status: "success" });
    } catch {
      toast({ description: "Сталась помилка!", status: "error" });
    } finally {
      setBusy(null);
    }
  }

  function onPick(type: BrandImageType, e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void run(type, () => uploadBrandImage(file, type));
  }

  return (
    <>
      {/* Sidebar logo — click to manage */}
      <Box
        as="button"
        type="button"
        onClick={onOpen}
        position="relative"
        role="group"
        borderRadius="md"
        overflow="hidden"
        title={"Редагувати логотип"}
      >
        {logoUrl ? (
          <Image
            src={logoUrl}
            alt={publishingHouse.name}
            boxSize="72px"
            objectFit="contain"
            bg="whiteAlpha.100"
          />
        ) : (
          <Center boxSize="72px" bg="whiteAlpha.100" flexDirection="column" gap={1}>
            <Icon as={ImagePlus} boxSize={5} opacity={0.7} />
            <Text fontSize="2xs" opacity={0.7} px={1} textAlign="center" noOfLines={2}>
              {"Завантажити логотип"}
            </Text>
          </Center>
        )}
        <Center
          position="absolute"
          inset={0}
          bg="blackAlpha.500"
          opacity={0}
          _groupHover={{ opacity: 1 }}
          transition="opacity 0.15s"
        >
          <Icon as={Pencil} boxSize={4} color="white" />
        </Center>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{"Редагувати логотип"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={6}>
              <BrandSection
                title={"Логотип"}
                imageUrl={logoUrl}
                aspect="square"
                busy={busy === "logo"}
                chooseLabel={"Завантажити логотип"}
                deleteLabel={"Видалити логотип"}
                onChoose={() => logoInput.current?.click()}
                onDelete={() => void run("logo", () => deleteBrandImage("logo"))}
              />
              <BrandSection
                title={"Банер"}
                imageUrl={bannerUrl}
                aspect="wide"
                busy={busy === "banner"}
                chooseLabel={"Завантажити банер"}
                deleteLabel={"Видалити банер"}
                onChoose={() => bannerInput.current?.click()}
                onDelete={() => void run("banner", () => deleteBrandImage("banner"))}
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" onClick={onClose}>
              {"Закрити"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <input
        ref={logoInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => onPick("logo", e)}
      />
      <input
        ref={bannerInput}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => onPick("banner", e)}
      />
    </>
  );
}

function BrandSection({
  title,
  imageUrl,
  aspect,
  busy,
  chooseLabel,
  deleteLabel,
  onChoose,
  onDelete,
}: {
  title: string;
  imageUrl?: string;
  aspect: "square" | "wide";
  busy: boolean;
  chooseLabel: string;
  deleteLabel: string;
  onChoose: () => void;
  onDelete: () => void;
}) {
  const hasImage = !!imageUrl;
  return (
    <Box>
      <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
        {title}
      </Text>
      <Flex gap={4} align="center">
        <Center
          w={aspect === "wide" ? "200px" : "96px"}
          h="96px"
          flexShrink={0}
          bg="gray.50"
          border="1px solid"
          borderColor="blackAlpha.200"
          borderRadius="md"
          overflow="hidden"
        >
          {hasImage ? (
            <Image src={imageUrl} alt={title} maxH="100%" maxW="100%" objectFit="contain" />
          ) : (
            <Icon as={ImagePlus} boxSize={6} color="gray.300" />
          )}
        </Center>
        <VStack align="stretch" spacing={2}>
          <Button
            size="sm"
            leftIcon={<Icon as={ImagePlus} boxSize={4} />}
            onClick={onChoose}
            isLoading={busy}
          >
            {chooseLabel}
          </Button>
          {hasImage && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="red"
              leftIcon={<Icon as={Trash2} boxSize={4} />}
              onClick={onDelete}
              isDisabled={busy}
            >
              {deleteLabel}
            </Button>
          )}
        </VStack>
      </Flex>
    </Box>
  );
}
